import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatBRL } from "@/lib/api";
import { Trophy, X, Crown } from "lucide-react";

/** Bell-like sound via WebAudio (church bell strike + shimmer). */
function playBell() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const partials = [
      { freq: 880, gain: 0.4, decay: 1.5 },
      { freq: 1320, gain: 0.25, decay: 1.2 },
      { freq: 1760, gain: 0.18, decay: 0.9 },
      { freq: 2640, gain: 0.10, decay: 0.7 },
      { freq: 440, gain: 0.20, decay: 2.2 },
    ];
    partials.forEach((p) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = p.freq;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(p.gain, now + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, now + p.decay);
      osc.connect(g).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + p.decay + 0.05);
    });
    setTimeout(() => {
      try {
        const ctx2 = new AudioCtx();
        const t2 = ctx2.currentTime;
        [660, 990, 1320].forEach((f, i) => {
          const o = ctx2.createOscillator();
          const g = ctx2.createGain();
          o.type = "sine";
          o.frequency.value = f;
          g.gain.setValueAtTime(0.0001, t2);
          g.gain.exponentialRampToValueAtTime(0.25 / (i + 1), t2 + 0.005);
          g.gain.exponentialRampToValueAtTime(0.0001, t2 + 1.2);
          o.connect(g).connect(ctx2.destination);
          o.start(t2);
          o.stop(t2 + 1.3);
        });
      } catch { /* noop */ }
    }, 380);
  } catch { /* noop */ }
}

/** Jingle dourado para meta batida: arpejo maestoso + acorde final grande. */
function playGoldenJingle() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    // Arpejo: C E G C E G C (D maior aberto)
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5, 1567.98, 2093.0];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = now + i * 0.13;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.55);
    });
    // Acorde final grande: C4 E4 G4 C5
    const finalT = now + notes.length * 0.13 + 0.1;
    [261.63, 329.63, 392.0, 523.25, 783.99].forEach((f) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, finalT);
      g.gain.exponentialRampToValueAtTime(0.22, finalT + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, finalT + 2.2);
      o.connect(g).connect(ctx.destination);
      o.start(finalT);
      o.stop(finalT + 2.3);
    });
  } catch { /* noop */ }
}

export default function CelebrationOverlay({ tvMode = false }) {
  const { lastEvent } = useRealtime() || {};
  const { user } = useAuth() || {};
  const [notif, setNotif] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === "inscricao") {
      setNotif(lastEvent);
      const colors = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#4F46E5", "#FFD700"];
      const end = Date.now() + 1200;
      (function frame() {
        confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
      confetti({ particleCount: 250, spread: 120, origin: { y: 0.4 }, colors });
      if (tvMode || user?.som_ativo !== false) playBell();
      const t = setTimeout(() => setNotif(null), 8000);
      return () => clearTimeout(t);
    }
    if (lastEvent.event === "meta_atingida") {
      setMeta(lastEvent);
      // Confete dourado massivo
      const gold = ["#FFD700", "#FFC300", "#FFB700", "#FFA000", "#FFF275", "#FF8C00"];
      const end = Date.now() + 5000;
      (function frame() {
        confetti({ particleCount: 8, angle: 60, spread: 70, origin: { x: 0 }, colors: gold });
        confetti({ particleCount: 8, angle: 120, spread: 70, origin: { x: 1 }, colors: gold });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
      confetti({ particleCount: 500, spread: 160, origin: { y: 0.35 }, colors: gold, scalar: 1.4 });
      if (tvMode || user?.som_ativo !== false) playGoldenJingle();
      const t = setTimeout(() => setMeta(null), 12000);
      return () => clearTimeout(t);
    }
  }, [lastEvent, user, tvMode]);

  return (
    <>
      {/* Notificação de nova inscrição */}
      {notif && (
        <div className="fixed top-20 right-4 z-[9999] max-w-sm fade-in-up" data-testid="celebration-overlay">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-emerald-400 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-blue-600 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest">
                <Trophy className="w-4 h-4" /> 🔔 Sino de vendas!
              </div>
              <button onClick={() => setNotif(null)} className="text-white/80 hover:text-white" data-testid="celebration-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="font-display text-lg font-bold text-slate-900 leading-tight">
                <span className="text-emerald-600">{notif.vendedora_nome}</span>
              </div>
              <div className="font-display text-base font-bold text-slate-800 mt-1">
                fechou {notif.quantidade} {notif.quantidade === 1 ? "inscrição" : "inscrições"}
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                em <b>{notif.municipio_nome}</b>
              </div>
              <div className="mt-2 font-display text-2xl font-extrabold tabular-nums text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-blue-600">
                {formatBRL(notif.valor)}
              </div>
              {notif.curso_nome && (
                <div className="text-xs text-slate-400 mt-1">{notif.curso_nome}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Golden Screen — meta atingida */}
      {meta && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center px-4 pointer-events-none"
          data-testid="meta-atingida-overlay"
          style={{
            background: "radial-gradient(ellipse at center, rgba(255,215,0,0.35) 0%, rgba(255,140,0,0.25) 40%, rgba(0,0,0,0.6) 100%)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div className="celebrate-anim bg-gradient-to-br from-yellow-300 via-amber-500 to-orange-600 rounded-3xl p-8 md:p-14 shadow-2xl border-4 border-white/40 max-w-3xl w-full text-center pointer-events-auto">
            <Crown className="w-16 h-16 md:w-24 md:h-24 text-white mx-auto mb-3 drop-shadow" />
            <div className="uppercase tracking-widest text-white/90 text-xs md:text-sm font-bold">Núcleo {meta.nucleo_nome}</div>
            <div className="font-display text-5xl md:text-7xl font-extrabold text-white leading-none mt-3 drop-shadow">
              META BATIDA!
            </div>
            <div className="mt-4 font-display text-3xl md:text-5xl font-extrabold text-white tabular-nums drop-shadow">
              {formatBRL(meta.realizado)}
            </div>
            <div className="text-white/90 text-lg md:text-2xl mt-1">de {formatBRL(meta.meta)} ✨</div>
            {!tvMode && (
              <button
                onClick={() => setMeta(null)}
                className="mt-6 text-white/80 hover:text-white text-xs underline"
                data-testid="meta-atingida-close"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
